import React, { useState, useEffect } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useDataProvider,
  useEditController,
  SelectInput,
  EditProps,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import { CcaAcademicYearResources, GetParam } from "utils/tools";
import moment from "moment";
import CustomEditToolbar from "components/CustomEditToolbar";
import { useField } from "react-final-form";
import CustomDatePickerInput from "components/CustomDatePickerInput";
import { reject } from "lodash";
import { ccaRateChartType } from "./const";
import { validateCcaRateChartCreation } from "./validation";
import CcaTbfCodeInput from "./components/CcaTbfCodeInput";
import CcaRateChartTypeInput from "./components/CcaRateChartTypeInput";
import CustomDateRangeInput from "../../components/CustomDateRangeInput";
import EditWrapper from "../../components/EditWrapper";
import PermissionLoading from "../../components/PermissionLoading";

interface CustomReferenceInputProps {
  param: any;
  disableForm: boolean;
  setIdCcaAcademicYear: any;
}

const CustomReferenceInput: React.FC<CustomReferenceInputProps> = ({
  param,
  disableForm,
  setIdCcaAcademicYear,
}) => {
  const startedAt = useField("startedAt");
  const endedAt = useField("endedAt");
  const invoiceIssuedAt = useField("invoiceIssuedAt");

  return (
    <ReferenceInput
      label="Select CCA Academic Year"
      source="ccaAcademicYearId"
      reference="ccaAcademicYears"
      validate={[required()]}
      filter={{ SchoolId: Number(param.get("schoolId")) }}
      onChange={({ target }) => {
        setIdCcaAcademicYear(target.value);
        startedAt.input.onChange("");
        endedAt.input.onChange("");
        invoiceIssuedAt.input.onChange("");
      }}
      disabled={disableForm}
    >
      <SelectInput optionText="name" optionId="id" />
    </ReferenceInput>
  );
};

const RateChartEdit = (props: EditProps) => {
  const [ccaRateCharts, setCcaRateCharts] = useState<ICcaRateChart[]>([]);
  const [idCcaAcademicYear, setIdCcaAcademicYear] = useState();
  const [status, setStatus] = useState<string>();
  const [disableForm, setDisableForm] = useState<boolean>(false);

  const param = GetParam();
  const provider = useDataProvider();
  const { record, loading } = useEditController(props);

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    { title: "Pricing", link: `/schools/${param.get("schoolId")}/show/5` },
    { title: "Edit", link: "" },
  ];

  useEffect(() => {
    provider.getOne("ccaRateCharts", { id: props.id }).then((result) => {
      setIdCcaAcademicYear(result.data.ccaAcademicYearId);
      setStatus(result.data.status);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== "active") {
      setDisableForm(true);
    } else {
      setDisableForm(false);
    }
  }, [status]);

  const transform = (data: any) => {
    switch (data.ccaRateChartType) {
      case ccaRateChartType.costPerTripAndStudentType:
        data.ccaRateChartCostPerTrip = {
          riderPrice: (Number(data.riderPrice) * 100).toFixed(0),
          nonRiderPrice: (Number(data.nonRiderPrice) * 100).toFixed(0),
        };
        break;
      case ccaRateChartType.costPerTripBasedOnArea:
        data.ccaRateChartAreaItems = [...data.itemsToSend];
        break;
      case ccaRateChartType.costPerWeek:
        data.ccaRateChartCostPerWeek = {
          price: (Number(data.price) * 100).toFixed(0),
        };
        break;
    }

    delete data.riderPrice;
    delete data.nonRiderPrice;
    delete data.itemsToSend;
    delete data.price;
    delete data.updatedAt;

    return data;
  };

  const ccaAcademicYearResources = CcaAcademicYearResources(idCcaAcademicYear);

  useEffect(() => {
    if (idCcaAcademicYear !== undefined) {
      provider
        .getList("ccaRateCharts", {
          filter: { CcaAcademicYearId: Number(idCcaAcademicYear) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then((result) => {
          setCcaRateCharts(result.data as ICcaRateChart[]);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCcaAcademicYear]);

  let excludeDates: IExcludeDates[] = [];
  let ccaRateChartsWithoutSelected = reject(ccaRateCharts, {
    id: Number(props.id),
  });

  ccaRateChartsWithoutSelected.forEach((ccaRateChart: ICcaRateChart) => {
    let temp: IExcludeDates = {
      startDate: ccaRateChart.startedAt,
      endDate: ccaRateChart.endedAt,
    };

    excludeDates.push(temp);
  });

  const formatRecord = (res: any) => {
    if (res) {
      if (res.startedAt) {
        res.startedAt = moment(res.startedAt).format("YYYY-MM-DD");
      }

      if (res.endedAt) {
        res.endedAt = moment(res.endedAt).format("YYYY-MM-DD");
      }

      if (res.invoiceIssuedAt) {
        res.invoiceIssuedAt = moment(res.invoiceIssuedAt).format("YYYY-MM-DD");
      }

      return res;
    }
  };

  if (loading) {
    return <PermissionLoading />;
  }

  return (
    <>
      <EditWrapper
        breadcrumb={breadcrumb}
        customName={{
          resourceName: "CCA Rate Chart",
          propertyName: "name",
        }}
        transform={transform}
        actions={false}
        {...props}
      >
        <SimpleForm
          record={formatRecord(record)}
          validate={(values: any) =>
            validateCcaRateChartCreation(values, excludeDates)
          }
          toolbar={
            <CustomEditToolbar
              redirectLink={`/schools/${param.get("schoolId")}/show/5`}
              isDeleteWithConfirm
            />
          }
          name="manageRateChart"
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School & CCA Academic Year</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                validate={[required()]}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomReferenceInput
                disableForm={disableForm}
                param={param}
                setIdCcaAcademicYear={setIdCcaAcademicYear}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CcaTbfCodeInput isDisabled={disableForm} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Pricing Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                source="name"
                label="Pricing Name"
                validate={required()}
                disabled={disableForm}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={12}>
              <CustomDateRangeInput
                fromSource="startedAt"
                toSource="endedAt"
                fromLabel="Pricing Start Date"
                toLabel="Pricing End Date"
                parentStartDate={moment
                  .utc(ccaAcademicYearResources?.startDate)
                  .format("yyyy-MM-DD")}
                fromMaxDate={moment
                  .utc(ccaAcademicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                toMaxDate={moment
                  .utc(ccaAcademicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                excludeDates={excludeDates}
                isDisabled={disableForm}
                gridItemWidth={3}
              />
            </Grid>

            <Grid item xs={3}>
              <CustomDatePickerInput
                name="invoiceIssuedAt"
                label="Invoice Issue Date"
                min={moment().format("Y-MM-DD")}
                disableValue={disableForm}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={12}>
              <CcaRateChartTypeInput
                schoolId={Number(param.get("schoolId"))}
                ccaRateChartId={props.id}
                isEdit={true}
                isDisabled={disableForm}
              />
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default RateChartEdit;
